import React, { useMemo } from 'react';
import { Table, Button, Space, Card, Select, DatePicker, Tag, Row, Col, Tooltip, Input, Popconfirm } from 'antd';
import { ReloadOutlined, DownloadOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatTimeStr, getMatrixBadgeConfig } from '../attendanceUtils';

const { Option } = Select;

// Cell and Header Styles
const cellStyle = { padding: '6px 8px', fontSize: '13px', height: '36px' };
const headerCellStyle = { padding: '8px 8px', fontSize: '13px', fontWeight: 'bold' };

export default function MonthlyMatrixTab({
  monthlyMatrixData = { matrix: [], dateList: [] },
  matrixMonth,
  setMatrixMonth,
  matrixPersonType,
  setMatrixPersonType,
  ayFilter,
  setAyFilter,
  clusterFilter,
  setClusterFilter,
  standardFilter,
  setStandardFilter,
  combinationFilter,
  setCombinationFilter,
  academicYears = [],
  availableClusters = [],
  availableStandards = [],
  availableCombinations = [],
  matrixLoading = false,
  recalculating = false,
  isStudio = false,
  onReload,
  onRecalculate,
  onExportCSV,
  onOpenAttModal
}) {
  // Collect distinct combinations for the table head filter
  const tableCombinations = useMemo(() => {
    const set = new Set();
    (monthlyMatrixData.matrix || []).forEach((item) => {
      if (item.combination) set.add(item.combination);
    });
    (availableCombinations || []).forEach((c) => {
      if (c) set.add(c);
    });
    return Array.from(set).filter(Boolean).sort();
  }, [monthlyMatrixData.matrix, availableCombinations]);

  // Helper for text column search in table headers
  const getColumnSearchProps = (dataIndex, placeholder) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          placeholder={`Search ${placeholder}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: 'block', width: 180 }}
          autoFocus
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 85 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              if (clearFilters) clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 85 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : '#bfbfbf', fontSize: 13 }} />
    ),
    onFilter: (value, record) => {
      const fieldVal = record[dataIndex] || (dataIndex === 'code' ? record.biometric_user_id : '');
      return fieldVal ? String(fieldVal).toLowerCase().includes(String(value).toLowerCase()) : false;
    }
  });

  // Construct dynamic date and fixed columns
  const columns = useMemo(() => {
    const isCandidate = matrixPersonType === 'CANDIDATE' || isStudio;

    const cols = [
      {
        title: 'CODE',
        dataIndex: 'code',
        key: 'code',
        fixed: 'left',
        width: 100,
        align: 'left',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        ...getColumnSearchProps('code', 'Code / PIN'),
        render: (text, record) => (
          <span style={{ fontSize: 13, color: 'inherit', fontWeight: 500 }}>
            {record.biometric_user_id || (text ? text.replace(/^(CAND-|STAFF-)/, '') : record.id)}
          </span>
        )
      },
      {
        title: 'NAME',
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        width: 180,
        align: 'left',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        ...getColumnSearchProps('name', 'Name'),
        render: (text) => <span style={{ fontSize: 13, color: 'inherit', fontWeight: 500 }}>{text}</span>
      }
    ];

    // Add COMBINATION column for candidates
    if (isCandidate) {
      cols.push({
        title: 'COMBINATION',
        dataIndex: 'combination',
        key: 'combination',
        fixed: 'left',
        width: 140,
        align: 'left',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        filters: tableCombinations.map((comb) => ({ text: comb, value: comb })),
        onFilter: (value, record) => record.combination === value,
        render: (val) => (
          val ? (
            <Tag color="geekblue" style={{ fontSize: 11, marginRight: 0 }}>
              {val}
            </Tag>
          ) : (
            <span style={{ color: '#aaa' }}>-</span>
          )
        )
      });
    }

    // Dynamic date columns with filters
    (monthlyMatrixData.dateList || []).forEach((dateStr) => {
      const d = dayjs(dateStr);
      const formattedDate = d.format('DD-MM-YYYY');
      const dayOfWeek = d.format('ddd');

      cols.push({
        title: (
          <Tooltip title={`${dayOfWeek}, ${formattedDate}`}>
            <div style={{ lineHeight: 1.2 }}>
              <div>{formattedDate}</div>
              <div style={{ fontSize: 10, color: '#888', fontWeight: 'normal' }}>{dayOfWeek}</div>
            </div>
          </Tooltip>
        ),
        dataIndex: ['dailyStatus', dateStr],
        key: dateStr,
        width: 110,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        filters: [
          { text: 'P - Present', value: 'P' },
          { text: 'A - Absent', value: 'A' },
          { text: 'HD - Half Day', value: 'HD' },
          { text: 'OT - Overtime', value: 'OT' },
          { text: 'L - Late', value: 'L' },
          { text: 'LE - Leave Early', value: 'LE' },
          { text: '- Not Recorded', value: '-' }
        ],
        onFilter: (value, record) => {
          const dailyObj = record.dailyStatus?.[dateStr];
          let rawCode = dailyObj?.code ? String(dailyObj.code).toUpperCase() : '-';
          if (rawCode === 'ABSENT') rawCode = 'A';
          else if (rawCode === 'HALF_DAY' || rawCode === 'HALF DAY') rawCode = 'HD';
          else if (rawCode === 'OVERTIME') rawCode = 'OT';
          else if (rawCode === 'LATE') rawCode = 'L';
          else if (rawCode === 'LEAVE_EARLY' || rawCode === 'LEAVE') rawCode = 'LE';
          else if (rawCode === 'PRESENT') rawCode = 'P';
          return rawCode === value;
        },
        render: (dailyObj, record) => {
          if (!dailyObj || dailyObj.code === '-' || !dailyObj.code) return <span style={{ color: '#aaa' }}>-</span>;
          const rawCode = String(dailyObj.code).toUpperCase();
          const badge = getMatrixBadgeConfig(rawCode);

          return (
            <Tooltip
              title={`${dateStr} (${dayOfWeek}): ${dailyObj.status || badge.title} | In: ${formatTimeStr(dailyObj.check_in)} | Out: ${formatTimeStr(dailyObj.check_out)} | Hrs: ${dailyObj.hours || 0}`}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: '100%',
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 'bold',
                  color: badge.color,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  onOpenAttModal(
                    {
                      ...dailyObj,
                      attendance_id: dailyObj.attendance_id || undefined,
                      id: record.id,
                      staff_id: (record.person_type === 'STAFF' || matrixPersonType === 'STAFF') ? record.id : undefined,
                      candidate_id: (record.person_type === 'CANDIDATE' || matrixPersonType === 'CANDIDATE') ? record.id : undefined,
                      person_type: record.person_type || matrixPersonType,
                      biometric_user_id: record.biometric_user_id || undefined,
                      academic_year_id: record.academic_year_id || undefined,
                      attendance_date: dateStr,
                      check_in_time: dailyObj.check_in,
                      check_out_time: dailyObj.check_out,
                      status: dailyObj.status && dailyObj.status !== 'NOT_RECORDED' ? dailyObj.status : 'PRESENT',
                      remarks: dailyObj.remarks || ''
                    },
                    matrixPersonType,
                    dayjs(dateStr)
                  );
                }}
              >
                {badge.text}
              </span>
            </Tooltip>
          );
        }
      });
    });

    // Summary columns (P, A, HD, OT, L, LE, Total Working Hours)
    cols.push(
      {
        title: 'P',
        key: 'summary_p',
        fixed: 'right',
        width: 55,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => (a.summary?.presentCount || 0) - (b.summary?.presentCount || 0),
        render: (_, record) => (
          <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{record.summary?.presentCount || 0}</span>
        )
      },
      {
        title: 'A',
        key: 'summary_a',
        fixed: 'right',
        width: 55,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => (a.summary?.absentCount || 0) - (b.summary?.absentCount || 0),
        render: (_, record) => (
          <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{record.summary?.absentCount || 0}</span>
        )
      },
      {
        title: 'HD',
        key: 'summary_hd',
        fixed: 'right',
        width: 55,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => (a.summary?.halfDayCount || 0) - (b.summary?.halfDayCount || 0),
        render: (_, record) => (
          <span style={{ color: '#faad14', fontWeight: 'bold' }}>{record.summary?.halfDayCount || 0}</span>
        )
      },
      {
        title: 'OT',
        key: 'summary_ot',
        fixed: 'right',
        width: 55,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => (a.summary?.overtimeCount || 0) - (b.summary?.overtimeCount || 0),
        render: (_, record) => (
          <span style={{ color: '#fa541c', fontWeight: 'bold' }}>{record.summary?.overtimeCount || 0}</span>
        )
      },
      {
        title: 'L',
        key: 'summary_l',
        fixed: 'right',
        width: 55,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => (a.summary?.lateCount || 0) - (b.summary?.lateCount || 0),
        render: (_, record) => (
          <span style={{ color: '#d46b08', fontWeight: 'bold' }}>{record.summary?.lateCount || 0}</span>
        )
      },
      {
        title: 'LE',
        key: 'summary_le',
        fixed: 'right',
        width: 55,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => (a.summary?.leaveEarlyCount || 0) - (b.summary?.leaveEarlyCount || 0),
        render: (_, record) => (
          <span style={{ color: '#13c2c2', fontWeight: 'bold' }}>{record.summary?.leaveEarlyCount || 0}</span>
        )
      },
      {
        title: 'HRS',
        key: 'summary_hrs',
        fixed: 'right',
        width: 65,
        align: 'center',
        onCell: () => ({ style: cellStyle }),
        onHeaderCell: () => ({ style: headerCellStyle }),
        sorter: (a, b) => parseFloat(a.summary?.totalWorkingHours || 0) - parseFloat(b.summary?.totalWorkingHours || 0),
        render: (_, record) => (
          <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{record.summary?.totalWorkingHours || 0}</span>
        )
      }
    );

    return cols;
  }, [monthlyMatrixData, matrixPersonType, isStudio, tableCombinations, onOpenAttModal]);

  const isCandidateType = matrixPersonType === 'CANDIDATE' || isStudio;

  return (
    <div>
      <Card
        bodyStyle={{ padding: 16 }}
        style={{
          marginBottom: 16,
          background: 'var(--panel-color)',
          borderColor: 'var(--border-color)',
          borderRadius: 8
        }}
      >
        <Row gutter={[12, 12]} align="middle" justify="space-between">
          <Col xs={24} lg={16}>
            <Space wrap size="middle">
              <DatePicker.MonthPicker
                value={matrixMonth}
                onChange={(m) => setMatrixMonth(m || dayjs())}
                format="MMMM YYYY"
                allowClear={false}
                style={{ width: 145 }}
              />
              {!isStudio && (
                <Select
                  value={matrixPersonType}
                  onChange={(val) => {
                    setMatrixPersonType(val);
                    if (val === 'STAFF') {
                      if (setStandardFilter) setStandardFilter('all');
                      if (setCombinationFilter) setCombinationFilter('all');
                    }
                  }}
                  style={{ width: 150 }}
                >
                  <Option value="STAFF">Staff Matrix</Option>
                  <Option value="CANDIDATE">Candidate Matrix</Option>
                </Select>
              )}
              <Select
                value={ayFilter}
                onChange={(val) => {
                  setAyFilter(val);
                  if (setClusterFilter) setClusterFilter('all');
                  if (setStandardFilter) setStandardFilter('all');
                  if (setCombinationFilter) setCombinationFilter('all');
                }}
                style={{ width: 160 }}
                placeholder="Academic Year"
              >
                <Option value="all">All Academic Years</Option>
                {academicYears.map((ay) => (
                  <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                    {ay.academic_year}
                  </Option>
                ))}
              </Select>
              {setClusterFilter && (
                <Select
                  value={clusterFilter}
                  onChange={(val) => {
                    setClusterFilter(val);
                    if (setStandardFilter) setStandardFilter('all');
                    if (setCombinationFilter) setCombinationFilter('all');
                  }}
                  style={{ width: 145 }}
                  placeholder="Cluster"
                >
                  {!isStudio && <Option value="all">All Clusters</Option>}
                  {availableClusters.map((cl) => (
                    <Option key={cl.cluster_id} value={String(cl.cluster_id)}>
                      {cl.cluster_name} {cl.cluster_code ? `(${cl.cluster_code})` : ''}
                    </Option>
                  ))}
                </Select>
              )}
              {isCandidateType && (
                <>
                  <Select
                    value={standardFilter}
                    onChange={(val) => {
                      setStandardFilter(val);
                      if (setCombinationFilter) setCombinationFilter('all');
                    }}
                    style={{ width: 135 }}
                    placeholder="Standard"
                  >
                    <Option value="all">All Standards</Option>
                    {availableStandards.map((st) => (
                      <Option key={st.standard_id} value={String(st.standard_id)}>
                        {st.standard_name}
                      </Option>
                    ))}
                  </Select>
                  <Select
                    value={combinationFilter || 'all'}
                    onChange={setCombinationFilter}
                    style={{ width: 155 }}
                    placeholder="Combination"
                  >
                    <Option value="all">All Combinations</Option>
                    {tableCombinations.map((comb) => (
                      <Option key={comb} value={comb}>
                        {comb}
                      </Option>
                    ))}
                  </Select>
                </>
              )}
              <Button
                icon={<ReloadOutlined />}
                onClick={onReload}
                loading={matrixLoading}
              >
                Refresh
              </Button>
              <Popconfirm
                title="Recalculate Monthly Attendance?"
                description={`Re-evaluates attendance for all days in ${matrixMonth ? matrixMonth.format('MMMM YYYY') : 'selected month'} from raw biometric punch logs. Continue?`}
                onConfirm={onRecalculate}
                okText="Yes, Recalculate"
                cancelText="Cancel"
                disabled={recalculating}
              >
                <Button
                  icon={<SyncOutlined spin={recalculating} />}
                  loading={recalculating}
                  title="Recalculate monthly matrix from raw biometric punch logs"
                >
                  Recalculate
                </Button>
              </Popconfirm>
              <Button
                type="primary"
                ghost
                icon={<DownloadOutlined />}
                onClick={onExportCSV}
              >
                Export Excel / CSV (Colored)
              </Button>
            </Space>
          </Col>
          <Col xs={24} lg={8} style={{ textAlign: 'right' }}>
            <Space wrap size={[6, 6]}>
              <Tag color="success">P = Present</Tag>
              <Tag color="error">A = Absent</Tag>
              <Tag color="warning">HD = Half Day</Tag>
              <Tag color="volcano">OT = Overtime</Tag>
              <Tag color="orange">L = Late</Tag>
              <Tag color="cyan">LE = Leave Early</Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={monthlyMatrixData.matrix || []}
        rowKey="id"
        loading={matrixLoading}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '100', '200'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
        }}
        scroll={{ x: 'max-content' }}
        bordered
        size="small"
        style={{ background: 'var(--panel-color)' }}
      />
    </div>
  );
}
